import {
    type HTMLAttributes,
    useMemo,
    type ReactNode,
    useState,
    useRef,
} from 'react'
import { Link } from 'react-router-dom'
import { APIDocumentType } from '@visorpro/client'
import type {
    APIDocument,
    APIProductModel,
    APIDataSet,
    APIDocumentListParams,
    PaginatedRequestQueryParams,
    DocumentQueueTasks,
} from '@visorpro/client'
import {
    MaterialReactTable,
    type MRT_SortingState,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_Updater,
} from 'material-react-table'
import {
    Switch,
    Chip,
    MenuItem,
    ListItemText,
    IconButton,
    Tooltip,
    type FilterOptionsState,
    type AutocompleteRenderOptionState,
    Box,
    CircularProgress,
    Select,
    type SelectChangeEvent,
    ButtonGroup,
    Button,
    Popover,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material'
import {
    ArrowDropDown,
    NoSim,
    Info,
    OpenInNew,
    Folder,
} from '@mui/icons-material'
import _ from 'lodash'
import fuzzysort from 'fuzzysort'
import { darken } from '@mui/material'
import { type DateTime } from 'luxon'
import { formatDate } from '../util/format'
import { type APIUpdateDocumentRequest } from '../stores/documents-store'
import { type SearchableSelectOption } from './searchable-select'
import { type DocumentUpload } from '../types'
import { useAdminMaterialReactTable } from './table/mui-table'
import { useMultiValueColumnDef } from './table/column-definition'
import { type DictionaryArray } from '../types'
import { DocumentUploadsTable } from './documents-upload-table'
import {
    MetadataDialog,
    type MetadataDialogProps,
} from './metadata/metadata-dialogs'
import { SplitButtons } from './split-button'
import { stringify } from 'csv-stringify/browser/esm/sync'
import { exportFile } from '../util/export'
import {
    createQueueTaskOptions,
    type CreateQueueTaskOption,
} from '../util/document-queue-tasks'
import { ModelChip } from './models-table'
import pMap from 'p-map'

export interface DocumentsTableProps {
    documents: DictionaryArray<APIDocument>
    dataSets: DictionaryArray<APIDataSet>
    models: DictionaryArray<APIProductModel>
    documentUploads?: DocumentUpload[]
    isLoading?: boolean
    showProgressBars?: boolean
    rowCount?: number
    pagination?: PaginatedRequestQueryParams
    filters?: Omit<APIDocumentListParams, 'sort_by'>
    sorting?: APIDocumentListParams['sort_by']
    isPrimaryBackgroundColor?: boolean
    metadataDialog?: Omit<MetadataDialogProps, 'open' | 'setOpen'>
    setPagination?: (pagination: PaginatedRequestQueryParams) => void
    setFilters?: (filters: Omit<APIDocumentListParams, 'sort_by'>) => void
    setSorting?: (sorting: string[]) => void
    uploadDocument?: (files: FileList) => Promise<void>
    uploadMetadata?: (file: File) => Promise<void>
    updateDocumentEnabled?: (id: string, isEnabled: boolean) => void
    updateDocument?: (
        documentId: string,
        body: APIUpdateDocumentRequest,
    ) => Promise<void>
    deleteDocument?: (documentId: string) => Promise<void>
    setDataSets?: (
        documentId: string,
        addedDatasetIds: string[],
        removedDataSetIds: string[],
    ) => Promise<void>
    setModels?: (
        documentId: string,
        addedModelIds: string[],
        removedModelIds: string[],
    ) => Promise<void>
    createQueueTask?: (
        kind: DocumentQueueTasks | DocumentQueueTasks[],
        documentIds: string[],
    ) => Promise<void>
}

export interface ModelSelectOption extends SearchableSelectOption {
    manufacturer?: string
    renderedLabel?: ReactNode
    renderedManufacturer?: ReactNode
}

const renderModelOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: ModelSelectOption,
    state: AutocompleteRenderOptionState,
) => (
    <MenuItem value={option.value} {...props} key={state.index}>
        <ListItemText
            primary={option.renderedLabel || option.label}
            secondary={option.renderedManufacturer || option.manufacturer}
            title={option.label}
            sx={{ textWrap: 'auto' }}
        />
    </MenuItem>
)

const filterModelOptions = (
    options: ModelSelectOption[],
    { inputValue }: FilterOptionsState<ModelSelectOption>,
) => {
    if (!inputValue || inputValue == '') return options

    const matchHighlight = (match: string, index: number) => (
        <Box
            component="span"
            sx={{
                backgroundColor: (theme) =>
                    darken(theme.palette.warning.dark, 0.25),
                borderRadius: '2px',
                color: (theme) => theme.palette.common.white,
                padding: '2px 1px',
            }}
            key={index}>
            {match}
        </Box>
    )
    const input = inputValue.trim().toLowerCase()
    const results = fuzzysort.go(input, options, {
        keys: ['label', 'manufacturer'],
    })
    return results.map((result) => {
        const highlightedLabel = result[0].highlight(matchHighlight)
        const highlightedManufacturer = result[1].highlight(matchHighlight)

        const renderedLabel =
            result[0].target == '' ? result.obj.label : highlightedLabel
        const renderedManufacturer =
            result[1].target == ''
                ? result.obj.manufacturer
                : highlightedManufacturer

        return {
            ...result.obj,
            renderedLabel,
            renderedManufacturer,
        }
    })
}

export const DocumentsTable = ({
    documents,
    rowCount,
    documentUploads,
    uploadDocument,
    uploadMetadata,
    isLoading,
    showProgressBars,
    isPrimaryBackgroundColor,
    pagination,
    setPagination,
    filters,
    setFilters,
    sorting,
    setSorting,
    updateDocumentEnabled,
    updateDocument,
    deleteDocument,
    dataSets,
    setDataSets,
    models,
    setModels,
    createQueueTask,
    metadataDialog,
}: DocumentsTableProps) => {
    const [isDocumentUploadsTableOpen, setIsDocumentUploadsTableOpen] =
        useState(false)
    const documentUploadsTableRef = useRef<HTMLDivElement>(null)
    const toggleDocumentUploadsTable = () =>
        setIsDocumentUploadsTableOpen((isOpen) => !isOpen)

    const [isMetadataDialogOpen, setIsMetadataDialogOpen] = useState(false)
    const [isMetadataUploading, setIsMetadataUploading] = useState(false)

    const documentFileInputRef = useRef<HTMLInputElement>(null)
    const metadataFileInputRef = useRef<HTMLInputElement>(null)

    const [queueTasksSubmittedSnackbarOpen, setQueueTasksSubmittedSnackbar] =
        useState(false)
    const onQueueTasksSubmittedSnackbarClose = () =>
        setQueueTasksSubmittedSnackbar(false)

    const [copiedToClipBoardSnackbarOpen, setCopiedToClipBoardSnackbar] =
        useState(false)
    const onCopiedToClipBoardSnackbarClose = () =>
        setCopiedToClipBoardSnackbar(false)

    const [isDeletingDocuments, setIsDeletingDocuments] = useState(false)

    const paginationState = useMemo(() => {
        if (pagination === undefined) return undefined

        return {
            pageIndex:
                pagination.offset && pagination.limit
                    ? pagination.offset / pagination.limit
                    : 0,
            pageSize: pagination.limit || 100,
        }
    }, [pagination])

    const onPaginationChange = (
        pagination: MRT_Updater<MRT_PaginationState>,
    ) => {
        if (typeof pagination === 'function') {
            const newPagination = pagination(table.getState().pagination)
            setPagination?.({
                offset: newPagination.pageIndex * newPagination.pageSize,
                limit: newPagination.pageSize,
            })
        } else {
            setPagination?.({
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
            })
        }
    }

    const columnFiltersState = useMemo(() => {
        if (filters === undefined) return undefined

        const columnFilters: MRT_ColumnFiltersState = []
        Object.entries(filters).forEach(([key, value]) => {
            columnFilters.push({ id: key, value })
        })

        return columnFilters
    }, [filters])

    const onColumnFiltersChange = (
        filters: MRT_Updater<MRT_ColumnFiltersState>,
    ) => {
        const oldFilters = table.getState().columnFilters
        let newFilters: MRT_ColumnFiltersState

        if (typeof filters === 'function') {
            newFilters = filters(oldFilters)
        } else {
            newFilters = filters
        }

        if (_.isEqual(newFilters, oldFilters)) return

        const filtersBody: Record<string, unknown> = {}
        newFilters.forEach((filter) => {
            filtersBody[filter.id] = filter.value
        })

        setFilters?.(filtersBody)
        table.firstPage()
    }

    const sortingState = useMemo(() => {
        if (sorting === undefined) return []

        return sorting.map((sort) => {
            if (sort.startsWith('-')) {
                return { id: sort.slice(1), desc: true }
            } else {
                return { id: sort, desc: false }
            }
        })
    }, [sorting])

    const onSortingChange = (sorting: MRT_Updater<MRT_SortingState>) => {
        let newSorting: MRT_SortingState
        const oldSorting = table.getState().sorting

        if (typeof sorting === 'function') {
            newSorting = sorting(oldSorting)
        } else {
            newSorting = sorting
        }

        if (_.isEqual(newSorting, oldSorting)) return

        const sortingBody = newSorting.map((sort) => {
            return sort.desc ? `-${sort.id}` : sort.id
        })
        setSorting?.(sortingBody)
    }

    const getDataSetOptions = (
        dataSets: APIDataSet[],
    ): SearchableSelectOption[] => {
        return dataSets.map((dataSet) => ({
            label: dataSet.name,
            value: dataSet.id,
        }))
    }

    const dataSetColumnDefs = useMultiValueColumnDef<APIDocument, APIDataSet>({
        fieldName: 'Datasets',
        optionsData: dataSets,
        renderChip: (dataset, index) => (
            <Link
                className="text-link"
                to={`/data-sets/${dataset.id}/documents`}
                key={index}>
                <Chip
                    label={dataset.name}
                    size="small"
                    title={dataset.name}
                    icon={<Folder />}
                    clickable
                />
            </Link>
        ),
        getOptions: getDataSetOptions,
        onEdit: setDataSets,
    })

    const getModelOptions = (models: APIProductModel[]): ModelSelectOption[] => {
        return models.map((model) => ({
            manufacturer: model.product_manufacturer?.name,
            label: model.name,
            value: model.id,
        }))
    }

    const {
        Cell: ModelsCell,
        Edit: ModelsEdit,
        Filter: ModelsFilter,
    } = useMultiValueColumnDef<APIDocument, APIProductModel>({
        fieldName: 'Models',
        optionsData: models,
        renderChip: (model, index) => (
            <ModelChip
                name={model.name}
                manufacturer={model.product_manufacturer?.name || ''}
                key={index}
            />
        ),
        getOptions: getModelOptions,
        onEdit: setModels,
        SearchableSelectProps: {
            renderOption: renderModelOption,
            filterOptions: filterModelOptions,
            renderTags: (
                value: ModelSelectOption[],
                getTagProps,
                _ownerState,
            ) => {
                return value.map((option, index) => {
                    const props = getTagProps({ index })
                    return (
                        <ModelChip
                            name={option.label}
                            manufacturer={option.manufacturer || ''}
                            {...props}
                            key={option.value}
                        />
                    )
                })
            },
            rowHeight: 53.06,
        },
    })

    const columns = useMemo<MRT_ColumnDef<APIDocument>[]>(
        () => [
            {
                accessorKey: 'id',
                id: 'id',
                header: 'ID',
                enableClickToCopy: true,
                size: 400,
                enableColumnFilter: true,
            },
            {
                accessorKey: 'title',
                id: 'title',
                header: 'Title',
                size: 600,
                enableColumnFilter: true,
                muiTableBodyCellProps: ({ cell }) => ({
                    title: cell.getValue<string>(),
                    sx: {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                    },
                }),
                ...(updateDocument && {
                    enableEditing: true,
                    muiEditTextFieldProps: ({ cell, row }) => ({
                        type: 'text',
                        label: 'Title',
                        required: true,
                        onBlur: (event) => {
                            const title = event.target.value
                            const id = row.id
                            if (title !== cell.getValue()) {
                                void updateDocument(id, { title })
                            }
                        },
                    }),
                }),
            },
            {
                accessorKey: 'original_filename',
                header: 'Original Filename',
                enableColumnFilter: true,
                enableClickToCopy: true,
            },
            {
                accessorKey: 'sha512_hash',
                header: 'Hash',
                enableClickToCopy: true,
                enableColumnFilter: true,
            },
            {
                accessorFn: (doc) => doc.data_sets ?? [],
                header: 'Datasets',
                id: 'data_set_ids',
                size: 300,
                grow: true,
                filterFn: 'objectId',
                enableSorting: false,
                ...dataSetColumnDefs,
            },
            {
                accessorFn: (document) =>
                    document.document_to_product_model?.map(
                        (model) => model.product_model,
                    ),
                header: 'Models',
                id: 'model_ids',
                size: 300,
                grow: true,
                Cell: ModelsCell,
                enableEditing: setModels !== undefined,
                Edit: ModelsEdit,
                enableColumnFilter: true,
                Filter: ModelsFilter,
                filterFn: 'objectId',
                enableSorting: false,
            },
            {
                accessorKey: '_count.sections',
                header: 'Sections',
                filterVariant: 'range-slider',
                enableSorting: false,
            },
            {
                accessorFn: (doc) => doc.versions?.length || 0,
                header: 'Versions',
                filterVariant: 'range-slider',
                enableSorting: false,
            },
            {
                accessorKey: 'type',
                header: 'Type',
                enableEditing: updateDocument !== undefined,
                Edit: ({ cell, row, table }) => {
                    const currentType = cell.getValue<APIDocumentType>()
                    const id = row.id
                    const documentTypes: string[] = Object.values(
                        APIDocumentType as Record<string, string>,
                    )

                    const [type, setType] = useState(currentType)
                    const typeRef = useRef(currentType)
                    const onChange = (event: SelectChangeEvent<string>) => {
                        const selectedType = event.target
                            .value as APIDocumentType
                        typeRef.current = selectedType
                        setType(selectedType)
                    }
                    const onClose = () => {
                        if (typeRef.current !== currentType) {
                            void updateDocument?.(id, {
                                type: typeRef.current,
                            })
                        }
                        table.setEditingCell(null)
                    }

                    return (
                        <Select
                            fullWidth
                            required
                            label="Document type"
                            size="small"
                            variant="standard"
                            value={type}
                            onChange={onChange}
                            onClose={onClose}>
                            {documentTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    )
                },
                enableColumnFilter: true,
                filterVariant: 'select',
                filterSelectOptions: Object.values(APIDocumentType),
            },
            {
                accessorFn: (doc) => doc.updated_at,
                header: 'Updated At',
                id: 'updated_at',
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
                filterVariant: 'datetime-range',
            },
            {
                accessorFn: (doc) => doc.created_at,
                header: 'Created At',
                id: 'created_at',
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
                filterVariant: 'datetime-range',
            },
        ],
        [
            dataSetColumnDefs,
            ModelsCell,
            ModelsEdit,
            ModelsFilter,
            setModels,
            updateDocument,
            updateDocumentEnabled,
        ],
    )

    const isManualPaginationFirstPage = pagination && pagination.offset == 0
    const isNotManualPagination = !pagination
    const isAppendingUploads =
        documentUploads &&
        (isManualPaginationFirstPage || isNotManualPagination)

    const rowCountTotal = useMemo(() => {
        if (isAppendingUploads && rowCount) {
            return rowCount + documentUploads.length
        }
        return rowCount
    }, [documentUploads, isAppendingUploads, rowCount])

    const table = useAdminMaterialReactTable(
        {
            columns,
            data: documents,
            rowCount: rowCountTotal,
            defaultColumn: {
                enableSorting: true,
            },
            displayColumnDefOptions: {
                'mrt-row-actions': {
                    size: 200,
                },
            },
            enableSorting: true,
            enableMultiSort: true,
            enableRowActions: true,
            renderRowActions: ({ row }) => {
                const id = row.id
                const url = row.original.url
                const preparation = row.original.preparation
                const isSplit = !!preparation?.split_at
                const isParsed = !!preparation?.parsed_at
                const isEmbedded = !!preparation?.embedded_at
                const isUnpreparable = preparation?.is_unpreparable
                const isDone = isSplit && isParsed && isEmbedded
                const isEnabled = row.original.is_enabled
                const onChange = () =>
                    updateDocumentEnabled?.(id, !row.original.is_enabled)

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '8px' }}>
                        <Link to={`/documents/${id}`}>
                            <Tooltip title="Details">
                                <span>
                                    <IconButton
                                        size="small"
                                        title="Open details">
                                        <Info fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Link>
                        <Link
                            to={String(url)}
                            target="_blank"
                            style={{
                                pointerEvents: !url ? 'none' : undefined,
                            }}>
                            <Tooltip title="Open PDF" aria-disabled={!url}>
                                <span>
                                    <IconButton size="small" disabled={!url}>
                                        <OpenInNew fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Link>
                        <Link to={`/documents/${row.id}/tasks`}>
                            <Tooltip title="Tasks">
                                <span>
                                    <IconButton size="small">
                                        {!isUnpreparable && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.2,
                                                }}>
                                                <DocumentPreparationStatus
                                                    isDone={isSplit}
                                                    isSuccess={isDone}
                                                />
                                                <DocumentPreparationStatus
                                                    isDone={isParsed}
                                                    isSuccess={isDone}
                                                />
                                                <DocumentPreparationStatus
                                                    isDone={isEmbedded}
                                                    isSuccess={isDone}
                                                />
                                            </Box>
                                        )}
                                        {isUnpreparable && (
                                            <NoSim
                                                sx={{
                                                    color: (theme) =>
                                                        theme.palette.error
                                                            .light,
                                                }}
                                            />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Link>
                        <Tooltip title={isEnabled ? 'Enabled' : 'Disabled'}>
                            <span>
                                <Switch
                                    color="success"
                                    size="small"
                                    checked={isEnabled}
                                    onChange={onChange}
                                    disabled={
                                        updateDocumentEnabled === undefined
                                    }
                                />
                            </span>
                        </Tooltip>
                    </Box>
                )
            },
            sortDescFirst: true,
            renderTopToolbarCustomActions: () => {
                const selectedRows = table.getSelectedRowModel().flatRows
                const selectedRowsOriginal = selectedRows.map(
                    (row) => row.original,
                )
                const onClickQueueTask = async (
                    taskKind: CreateQueueTaskOption,
                ) => {
                    if (selectedRows.length === 0) return
                    if (!createQueueTask) return

                    const documentIds = selectedRowsOriginal.map(
                        (row) => row.id as string,
                    )
                    await createQueueTask(taskKind.value, documentIds)

                    setQueueTasksSubmittedSnackbar(true)
                }

                const onDocumentUploadClicked = () =>
                    documentFileInputRef.current?.click()
                const onMetadataUploadClicked = () =>
                    metadataFileInputRef.current?.click()

                const onDocumentFileInputChange = (
                    event: React.ChangeEvent<HTMLInputElement>,
                ) => {
                    const files = event.target.files
                    if (!files) return
                    void uploadDocument?.(files)
                    event.target.value = ''
                }
                const onMetadataFileInputChange = async (
                    event: React.ChangeEvent<HTMLInputElement>,
                ) => {
                    setIsMetadataDialogOpen(false)
                    setIsMetadataUploading(true)
                    const files = event.target.files
                    if (!files || files.length === 0) return
                    await uploadMetadata?.(files[0])
                    setIsMetadataDialogOpen(true)
                    setIsMetadataUploading(false)
                    event.target.value = ''
                }

                const onExportToCSV = () => {
                    const data = stringify(selectedRowsOriginal, {
                        header: true,
                        columns: [
                            { key: 'id', header: 'User ID' },
                            { key: 'title', header: 'Title' },
                            { key: 'original_filename', header: 'Original Filename' },
                            { key: 'sha512_hash', header: 'Hash' },
                            { key: 'type', header: 'Type' },
                            {
                                key: 'created_at',
                                header: 'Created At',
                            },
                            {
                                key: 'updated_at',
                                header: 'Updated At',
                            },
                        ],
                    })
                    exportFile('documents.csv', [data], 'text/csv')
                }
                const onExportToJSON = () => {
                    const data = JSON.stringify(selectedRowsOriginal, null, 2)
                    exportFile('documents.json', [data], 'application/json')
                }

                const onDeleteDocument = async () => {
                    setIsDeletingDocuments(true)
                }

                return (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {uploadDocument && (
                            <Box ref={documentUploadsTableRef}>
                                <ButtonGroup>
                                    <Button onClick={onDocumentUploadClicked}>
                                        Upload documents
                                    </Button>
                                    <Button
                                        sx={{ width: 16, px: 0 }}
                                        onClick={toggleDocumentUploadsTable}>
                                        {documentUploads &&
                                            documentUploads.length > 0 ? (
                                            <Box
                                                position="relative"
                                                display="inline-flex">
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={
                                                        documentUploadsProgress
                                                    }
                                                    size={16}
                                                    thickness={8}
                                                    color="warning"
                                                    sx={{ zIndex: 2 }}
                                                />
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={100}
                                                    size={16}
                                                    thickness={8}
                                                    sx={{
                                                        color: (theme) =>
                                                            theme.palette
                                                                .grey[600],
                                                        position: 'absolute',
                                                        left: 0,
                                                        zIndex: 1,
                                                    }}
                                                />
                                            </Box>
                                        ) : (
                                            <ArrowDropDown />
                                        )}
                                    </Button>
                                </ButtonGroup>

                                <input
                                    type="file"
                                    style={{ display: 'none' }}
                                    accept="application/pdf"
                                    multiple
                                    ref={documentFileInputRef}
                                    onChange={onDocumentFileInputChange}
                                />
                            </Box>
                        )}
                        {uploadMetadata && (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={onMetadataUploadClicked}
                                    disabled={isMetadataUploading}>
                                    Upload Metadata
                                </Button>

                                <input
                                    type="file"
                                    style={{ display: 'none' }}
                                    accept="text/csv"
                                    ref={metadataFileInputRef}
                                    onChange={onMetadataFileInputChange}
                                />
                            </>
                        )}
                        {deleteDocument && (
                            <Button
                                variant="outlined"
                                onClick={onDeleteDocument}
                                disabled={selectedRows.length < 1 || isLoading || showProgressBars}
                            >
                                {selectedRows.length > 1
                                    ? `Delete ${selectedRows.length} documents`
                                    : 'Delete document'}
                            </Button>
                        )}
                        {createQueueTask && (
                            <SplitButtons
                                disabled={selectedRows.length < 1}
                                popperWidth={225}>
                                {createQueueTaskOptions.map((option, index) => (
                                    <Button
                                        key={index}
                                        onClick={() => onClickQueueTask(option)}
                                        disabled={
                                            isLoading || showProgressBars
                                        }>
                                        {option.label}
                                    </Button>
                                ))}
                            </SplitButtons>
                        )}

                        <SplitButtons
                            disabled={
                                selectedRows.length < 1 ||
                                isLoading ||
                                showProgressBars
                            }>
                            <Button onClick={onExportToCSV}>
                                Export to CSV
                            </Button>
                            <Button onClick={onExportToJSON}>
                                Export to JSON
                            </Button>
                        </SplitButtons>
                    </Box>
                )
            },
            getRowId: (doc) => doc.id,
            ...(!!setPagination && {
                manualPagination: true,
                onPaginationChange,
            }),
            ...(!!setFilters && {
                manualFiltering: true,
                onColumnFiltersChange,
            }),
            ...(!!setSorting && {
                manualSorting: true,
                onSortingChange: onSortingChange,
            }),
            state: {
                isLoading,
                showProgressBars,
                ...(!!pagination &&
                    !!paginationState && { pagination: paginationState }),
                ...(!!filters &&
                    !!columnFiltersState && {
                    columnFilters: columnFiltersState,
                }),
                ...(!!sorting && !!sortingState && { sorting: sortingState }),
            },
            isPrimaryBackgroundColor,
        },
        'documents-table',
    )

    const documentUploadsProgress = useMemo(() => {
        if (!documentUploads) return 0

        let total = 0
        let completed = 0

        for (const documentUpload of documentUploads) {
            completed += documentUpload.progress
            total += 100
        }

        return (completed / total) * 100
    }, [documentUploads])

    const onDeleteDocumentConfirm = async (documentIds: string[]) => {
        if (deleteDocument === undefined) return

        await pMap(
            documentIds,
            async (documentId) => {
                try {
                    await deleteDocument(documentId)
                } catch (error) {
                    console.error(
                        `Failed to delete document ${documentId}: `,
                        error,
                    )
                }
            },
            { concurrency: 5 },
        )
        table.resetRowSelection()
        setIsDeletingDocuments(false)
    }

    return (
        <>
            <MaterialReactTable table={table} />
            <Popover
                open={isDocumentUploadsTableOpen}
                anchorEl={documentUploadsTableRef.current}
                onClose={toggleDocumentUploadsTable}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transitionDuration={100}
                slotProps={{
                    paper: {
                        sx: {
                            width: '700px',
                            height: '400px',
                            overflowY: 'auto',
                        },
                    },
                }}>
                <DocumentUploadsTable
                    documentUploads={documentUploads ?? []}
                    dataSets={dataSets}
                />
            </Popover>
            {metadataDialog && (
                <MetadataDialog
                    open={isMetadataDialogOpen}
                    setOpen={setIsMetadataDialogOpen}
                    {...metadataDialog}
                />
            )}
            <DeleteDocumentDialog
                open={isDeletingDocuments}
                setOpen={setIsDeletingDocuments}
                selectedDocuments={table
                    .getSelectedRowModel()
                    .flatRows.map((row) => row.original as APIDocument)}
                deleteDocuments={onDeleteDocumentConfirm}
            />
            <Snackbar
                open={queueTasksSubmittedSnackbarOpen}
                autoHideDuration={2000}
                onClose={onQueueTasksSubmittedSnackbarClose}
                message="Submitted queue tasks"
            />
            <Snackbar
                open={copiedToClipBoardSnackbarOpen}
                autoHideDuration={2000}
                onClose={onCopiedToClipBoardSnackbarClose}
                message="Copied to clipboard"
            />
        </>
    )
}

interface DeleteDocumentDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    selectedDocuments: APIDocument[]
    deleteDocuments: (documentIds: string[]) => void
}

const DeleteDocumentDialog = ({
    open,
    setOpen,
    selectedDocuments,
    deleteDocuments,
}: DeleteDocumentDialogProps) => {
    const onDelete = () => {
        deleteDocuments(selectedDocuments.map((document) => document.id))
        onClose()
    }
    const onClose = () => setOpen(false)

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Delete documents</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <div>
                        Are you sure you want to delete the following{' '}
                        {selectedDocuments.length} document{selectedDocuments.length !== 1 ? 's' : ''}?
                    </div>
                    <Box
                        sx={{
                            marginTop: 1,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                        }}>
                        {selectedDocuments.map((document, index) => (
                            <Chip
                                label={document.title || document.original_filename}
                                title={document.title || document.original_filename}
                                key={index}
                            />
                        ))}
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={onDelete}>
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const DocumentPreparationStatus = ({
    isDone = false,
    isSuccess = false,
}: {
    isDone?: boolean
    isSuccess?: boolean
}) => {
    return (
        <Box
            sx={{
                borderRadius: '999px',
                height: 12,
                width: 4,
                bgcolor: (theme) =>
                    isSuccess
                        ? theme.palette.success.main
                        : isDone
                            ? theme.palette.success.light
                            : theme.palette.grey[500],
            }}></Box>
    )
}
